<template>
  <div v-if="activeRoute" class="flex flex-col h-full overflow-hidden bg-[hsl(var(--card))]">

    <!-- Шапка: в компактном режиме (моб.) сюда же выносятся название и
         статус вместо отдельной строки ниже, для экономии высоты.
         touch-action:none задаётся заранее (не через preventDefault), т.к.
         попытка отменить жест постфактум проигрывает гонку с нативным
         распознавателем браузера и выглядит как баг - MobileRouteDetailSheet
         .vue должен безраздельно владеть этой зоной для ресайза.
         data-sheet-drag-handle помечает её как перетаскиваемую в любую
         сторону на любом уровне, в отличие от контента ниже. -->
    <!-- Шапка (не прокручивается): назад, название, сложность и строка
         действий - всегда на виду, в том числе в свёрнутой шторке. На мобильном
         это зона перетаскивания шторки (data-sheet-drag-handle); touch-action
         задаётся заранее - иначе нативный жест браузера выигрывает гонку. -->
    <div
      ref="headerRef"
      data-route-header
      class="shrink-0 px-4 pt-2 pb-3 bg-sheet border-b-[1.5px] border-border"
      :style="compact ? 'touch-action: none' : undefined"
      v-bind="compact ? { 'data-sheet-drag-handle': true } : {}"
    >
      <div class="flex items-start gap-2">
        <!-- «Назад» - в поиск, если карточку открыли оттуда, иначе в покой;
             крестик - всегда в покой (useSearchNavigation). -->
        <button
          type="button"
          data-card-back
          @click="$emit('back')"
          class="-ml-2 shrink-0 w-10 h-10 flex items-center justify-center rounded-full text-ink2 hover:bg-accent hover:text-foreground active:bg-accent"
          :aria-label="$t('routeDetails.back')"
        >
          <ChevronLeft class="w-6 h-6" :stroke-width="2" aria-hidden="true" />
        </button>
        <div class="flex-1 min-w-0">
          <h2 class="text-[19px] leading-tight line-clamp-2">{{ routeName.title }} <VerifiedMark :verified="!isCommunity" :size="18" class="-mt-0.5" /></h2>
          <p v-if="routeName.code" class="text-[12px] text-ink2 num">{{ routeName.code }}</p>
          <!-- Главное компактно: длина, набор, время - видно и в свёрнутой шторке
               (автор и описание - ниже, в теле «Обзора»). -->
          <p data-header-summary class="mt-1.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[14px]">
            <span v-if="activeRoute.distance_km != null" class="inline-flex items-center gap-1 num font-semibold"><Ruler class="w-4 h-4 text-ink2" aria-hidden="true" />{{ activeRoute.distance_km.toFixed(1) }} {{ $t('units.km') }}</span>
            <span v-if="activeRoute.elevation_gain_m != null" class="inline-flex items-center gap-1 num text-ink2"><TrendingUp class="w-4 h-4" aria-hidden="true" />{{ Math.round(activeRoute.elevation_gain_m) }} {{ $t('units.m') }}</span>
            <span v-if="formattedDuration" data-header-duration class="inline-flex items-center gap-1 num text-ink2"><Clock class="w-4 h-4" aria-hidden="true" />{{ formattedDuration }}</span>
          </p>
        </div>
        <button
          v-if="activeRoute.technical_grade"
          type="button"
          class="shrink-0 inline-flex items-center gap-1"
          :aria-label="$t('gradeModal.title')"
          @click="showGradeModal = true"
        >
          <GradeBadge :grade="activeRoute.technical_grade" />
          <Info class="w-3.5 h-3.5 text-ink2" aria-hidden="true" />
        </button>
        <button
          type="button"
          data-card-close
          class="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-muted text-ink2 hover:bg-accent hover:text-foreground"
          :aria-label="$t('routeDetails.close')"
          @click="$emit('close')"
        >
          <X class="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
      <div role="tablist" :aria-label="$t('routeDetails.tabs.label')" class="mt-2.5 -mx-1 flex gap-1.5 overflow-x-auto scrollbar-hide px-1">
        <button
          v-for="tb in tabDefs"
          :key="tb.key"
          type="button" role="tab" :data-tab="tb.key" :aria-selected="tab === tb.key"
          class="shrink-0 h-8 px-3.5 rounded-full text-[13px] font-semibold inline-flex items-center gap-1.5 transition-colors"
          :class="tab === tb.key ? 'bg-forest text-white' : 'bg-paper2 text-foreground hover:bg-accent'"
          @click="selectTab(tb.key)"
        >{{ tb.label }}
          <!-- «Отзывы»: звезда и оценка (число оценок в скобках); пустая звезда и прочерк, пока оценок нет. -->
          <span v-if="tb.key === 'reviews'" data-header-rating class="inline-flex items-center gap-0.5">
            <RatingStar :rating="hasRating ? Number(activeRoute.rating) : 0" />
            <template v-if="hasRating"><span class="num">{{ Number(activeRoute.rating).toFixed(1) }}</span><span class="num text-[11px] font-medium opacity-80">({{ activeRoute.rating_count }})</span></template>
            <template v-else><span aria-hidden="true">—</span><span class="sr-only">{{ $t('reviews.noRatings') }}</span></template>
          </span>
          <span v-else-if="tb.count" class="num text-[11px] font-medium opacity-80">{{ tb.count }}</span></button>
        <button
          type="button" role="tab" data-tab="photos" disabled aria-disabled="true"
          class="shrink-0 h-8 px-3.5 rounded-full text-[13px] font-semibold inline-flex items-center gap-1.5 bg-paper2 text-ink2 opacity-60 cursor-not-allowed"
          :title="$t('routeDetails.tabs.soon')"
        >{{ $t('routeDetails.tabs.photos') }}<span class="text-[10px] font-medium">{{ $t('routeDetails.tabs.soon') }}</span></button>
      </div>
      <!-- На мобильном (compact) эти кнопки - в нижней панели вместо вкладок
           (store/routeActions.ts, App.vue). -->
      <RouteActionRow
        v-if="!compact"
        class="mt-3"
        :saved="isSaved"
        :can-download="canDownload"
        :has-start="!!directions"
        menu-down
        @save="auth.isAuthenticated ? toggleSave() : loginGate.open(saveRoute)"
        @share="shareRoute"
        @copy-start="copyStart"
        @open-2gis="open2gis"
        @open-google-maps="openGoogleMaps"
        @register="openRegister"
        @download="openGpxDownload"
      />
      <p v-if="toastMessage" role="status" class="mt-2 text-[12px] font-semibold text-forest">{{ toastMessage }}</p>
    </div>

    <!-- Скроллируемое тело: пока scrollLocked активен, overflow и
         touch-action оба блокируются - драг по контенту на мобильном
         перехватывает MobileRouteDetailSheet.vue для ресайза шторки. -->
    <div
      ref="scrollBodyRef"
      class="flex-1"
      :class="scrollLocked ? 'overflow-hidden' : 'overflow-y-auto'"
      :style="{ touchAction: scrollLocked ? 'none' : 'auto' }"
    >
      <!-- Блок статусов измеряется (ResizeObserver, см. script) -
           под этот блок подгоняется компактная высота мобильной шторки. -->
      <div ref="compactSummaryRef" data-compact-summary class="px-4 space-y-3" :class="hasStatusNotes && 'pt-3'">
        <p v-if="activeRoute.status && activeRoute.status !== 'active'" class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-trail">
          <AlertTriangle class="w-4 h-4" aria-hidden="true" />{{ statusLabel(activeRoute.status) }}
        </p>
      </div>
      <template v-if="tab === 'overview'">
      <section v-if="profileChart" data-elevation-hero class="px-4 pt-4 pb-1">
        <div class="flex items-baseline justify-between mb-2">
          <h3 class="text-[15px]">{{ $t('routeDetails.elevationProfile') }}</h3>
          <span class="num text-[13px] font-semibold text-ink2">{{ formatProfileRange(activeRoute) }}</span>
        </div>
        <div class="flex items-stretch gap-1.5">
          <!-- Подписи оси высоты - обычный HTML, а не SVG <text>: при
               preserveAspectRatio="none" ниже текст исказился бы
               неравномерно (та же причина, почему бейдж курсора тоже HTML,
               см. ниже). Позиционируются в процентах от высоты этой
               колонки, которая должна совпадать с h-28 у SVG. -->
          <div class="relative shrink-0 h-36" style="width: 28px">
            <span
              v-for="tick in elevationTicks"
              :key="`y-${tick.value}`"
              class="absolute right-0 -translate-y-1/2 text-[10px] leading-none text-[hsl(var(--muted-foreground))] whitespace-nowrap"
              :style="{ top: `${tick.pct}%` }"
            >{{ tick.label }}</span>
          </div>

          <div class="flex-1 min-w-0">
            <!-- relative-обёртка, чтобы точка курсора позиционировалась
                 поверх SVG в процентных координатах. data-sheet-no-drag
                 исключает эту область из общего жеста ресайза
                 MobileRouteDetailSheet, чтобы скраббинг профиля не
                 конкурировал с ним за один и тот же указатель. -->
            <div class="relative" data-sheet-no-drag>
              <svg
                ref="profileSvgRef"
                class="w-full h-36 overflow-visible cursor-crosshair select-none"
                style="touch-action: none"
                viewBox="0 0 320 96"
                preserveAspectRatio="none"
                role="img"
                :aria-label="$t('routeDetails.elevationProfileCursorAria')"
                @pointerdown="onProfilePointerDown"
              >
                <defs>
                  <!-- id генерируется на экземпляр, а не статичный
                       "rdp-fill": MobileRouteDetailSheet.vue рендерит свою
                       RouteDetailsPanel рядом с десктопным <aside> (тот
                       просто скрыт CSS ниже md:, но остаётся смонтированным)
                       - при общем id url(#rdp-fill) резолвился
                       непредсказуемо между ними, из-за чего заливка
                       пропадала именно на мобильном. -->
                  <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="hsl(158,38%,36%)" stop-opacity="0.4" />
                    <stop offset="100%" stop-color="hsl(158,38%,36%)" stop-opacity="0.06" />
                  </linearGradient>
                </defs>
                <!-- Сетка - тонкая и полупрозрачная, чтобы читалась как
                     разметка, а не данные (прямые линии безопасны при
                     preserveAspectRatio="none": искажаются от неравномерного
                     масштабирования только фигуры с кривизной, например
                     текст или круги). -->
                <line
                  v-for="tick in elevationTicks"
                  :key="`gy-${tick.value}`"
                  x1="0" :y1="tick.y" x2="320" :y2="tick.y"
                  stroke="hsl(220,10%,44%)" stroke-opacity="0.15" stroke-width="0.6"
                />
                <line
                  v-for="tick in distanceTicks"
                  :key="`gx-${tick.value}`"
                  :x1="tick.x" y1="6" :x2="tick.x" y2="90"
                  stroke="hsl(220,10%,44%)" stroke-opacity="0.15" stroke-width="0.6"
                />
                <path :d="profileChart.areaPath" :fill="`url(#${gradientId})`" />
                <path :d="profileChart.linePath" fill="none" stroke="hsl(158,38%,36%)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                <g v-for="m in profileChart.markers" :key="`${m.kind}-${m.distance_km}`">
                  <line :x1="m.x" :x2="m.x" y1="4" y2="88" stroke="hsl(158,38%,36%)" stroke-opacity="0.12" stroke-width="1" stroke-dasharray="4 4" />
                </g>
                <line
                  v-if="profileCursor"
                  :x1="profileCursor.x" :x2="profileCursor.x" y1="4" y2="88"
                  stroke="hsl(158,38%,30%)" stroke-opacity="0.45" stroke-width="1.5"
                />
                <line
                  v-if="profileCursor"
                  x1="0" x2="320" :y1="profileCursor.y" :y2="profileCursor.y"
                  stroke="hsl(158,38%,30%)" stroke-opacity="0.45" stroke-width="1.5"
                />
              </svg>
              <!-- Контрольные точки (старт/финиш/ночёвки/привалы) - HTML-кнопки, а не SVG
                   <circle>: при preserveAspectRatio="none" круг растянулся бы в овал.
                   Тап показывает длину/набор/спуск от предыдущей точки. -->
              <button
                v-for="(m, i) in profileChart.markers"
                :key="`dot-${m.kind}-${m.distance_km}`"
                type="button"
                data-profile-marker
                class="absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                :style="{ left: `${(m.x / 320) * 100}%`, top: `${(m.y / 96) * 100}%` }"
                :aria-label="m.label"
                :aria-pressed="selectedMarkerIdx === i"
                @pointerdown.stop
                @click="selectedMarkerIdx = selectedMarkerIdx === i ? null : i"
              >
                <span
                  class="flex items-center justify-center rounded-full border-2 border-white shadow-sm"
                  :class="[markerIcon(m.kind) ? 'h-5 w-5' : 'h-3 w-3', selectedMarkerIdx === i && 'ring-2 ring-[hsl(var(--primary))]']"
                  :style="{ backgroundColor: markerColor(m.kind) }"
                >
                  <component :is="markerIcon(m.kind)" v-if="markerIcon(m.kind)" class="h-3 w-3 text-white" aria-hidden="true" />
                </span>
              </button>
              <!-- Точка курсора - обычный HTML-круг, а не SVG <circle>, в
                   реальных пикселях: фигура с кривизной (в отличие от
                   прямых линий сетки/курсора выше) превращается в эллипс
                   при preserveAspectRatio="none", если соотношение сторон
                   отрисованного SVG отличается от viewBox 320:96 - а оно
                   почти всегда отличается. -->
              <div
                v-if="profileCursor"
                class="absolute w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-card border-2 border-[hsl(158,38%,36%)] shadow-sm pointer-events-none"
                :style="{ left: `${(profileCursor.x / 320) * 100}%`, top: `${(profileCursor.y / 96) * 100}%` }"
              />
              <!-- Плавающий бейдж курсора - одна строка с высотой;
                   остальные цифры участка «старт-курсор» - в полосе статистики
                   ниже, пока курсор тащат. Располагается сбоку от точки курсора, а не над ней:
                   справа, пока точка в левой половине графика, слева после
                   середины, чтобы бейдж никогда не перекрывал свою же
                   точку. Фиксированная ширина - при разворачивании бейдж
                   растёт только вниз. Маленький повёрнутый квадрат -
                   хвостик "speech bubble", обращённый к точке. -->
              <div
                v-if="profileCursor"
                class="absolute -translate-y-1/2 w-[88px] px-1.5 py-1.5 rounded-md bg-[hsl(var(--card))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] text-[10px] font-semibold leading-tight shadow-md select-none"
                :style="cursorBadgeStyle"
              >
                <p class="flex items-center gap-1">
                  <Mountain class="w-3 h-3 shrink-0" />{{ Math.round(profileCursor.elevationM) }}{{ $t('units.m') }}
                </p>
                <span
                  class="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rotate-45 bg-[hsl(var(--card))] border border-[hsl(var(--border))]"
                  :style="cursorBadgeSide === 'right' ? { left: '-3px' } : { right: '-3px' }"
                />
              </div>
            </div>

            <!-- Подписи оси дистанции - та же причина HTML вместо SVG
                 <text>, что и у оси высоты выше. -->
            <div class="relative h-3 mt-0.5">
              <span
                v-for="tick in distanceTicks"
                :key="`x-${tick.value}`"
                class="absolute -translate-x-1/2 text-[10px] leading-none text-[hsl(var(--muted-foreground))] whitespace-nowrap"
                :style="{ left: `${Math.min(Math.max(tick.pct, 4), 96)}%` }"
              >{{ tick.label }}</span>
            </div>
          </div>
        </div>
        <p v-if="selectedLeg" data-profile-leg class="num mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12.5px] font-semibold">
          <span>{{ selectedLeg.label }}</span>
          <span>{{ formatDistance(selectedLeg.distanceKm) }}</span>
          <span class="inline-flex items-center gap-0.5"><TrendingUp class="h-3 w-3" aria-hidden="true" />{{ formatElevation(selectedLeg.gainM) }}</span>
          <span class="inline-flex items-center gap-0.5"><TrendingDown class="h-3 w-3" aria-hidden="true" />{{ formatElevation(selectedLeg.lossM) }}</span>
        </p>
      </section>

      <!-- Легенда цифр: как поля топокарты - без рамок, крупные числа,
           тонкие разделители. -->
      <dl data-stat-strip class="mx-4 mt-4 flex border-y border-border py-3">
        <div v-for="(item, i) in statItems" :key="item.label" class="min-w-0 flex-1 px-1 text-center" :class="i > 0 && 'border-l border-border'" :title="item.hint">
          <dd class="num whitespace-nowrap text-[16px] font-semibold leading-none">{{ item.value }}<span v-if="item.unit" class="ml-px text-[10px] font-normal text-ink2">{{ item.unit }}</span></dd>
          <dt class="mt-1.5 inline-flex items-center gap-0.5 text-[11px] text-ink2">{{ item.label }}<InfoTooltip v-if="item.tip" :text="item.tip" /></dt>
        </div>
      </dl>

      <div class="px-4 pt-3 space-y-0.5">
          <p v-if="isCommunity" data-community class="text-[13px] leading-tight text-ink2">
            <template v-if="activeRoute.author_name">{{ $t('routes.source.author') }}:
              <a v-if="activeRoute.author_username" data-author-link :href="authorProfileHref" @click="onAuthorClick" class="font-semibold text-primary hover:underline">{{ activeRoute.author_name }}</a>
              <span v-else class="font-semibold">{{ activeRoute.author_name }}</span>
              ·
            </template>{{ $t('routes.source.unverifiedShort') }}
          </p>
          <!-- Каталожный маршрут: автор - EcoWay, без ссылки. -->
          <p v-else data-catalogue-author class="text-[13px] leading-tight text-ink2">{{ $t('routes.source.author') }}: <span class="font-semibold">EcoWay</span></p>
      </div>
      <!-- Описание (до 250 знаков): под профилем высот и цифрами;
           длинное сворачивается в три строки. -->
      <section v-if="activeRoute.description" data-route-description class="px-4 pt-3">
        <p class="whitespace-pre-line break-words text-[15px] leading-snug" :class="!descExpanded && descLong && 'line-clamp-3'">{{ activeRoute.description }}</p>
        <button v-if="descLong" type="button" class="mt-1 text-[13px] font-semibold text-primary" @click="descExpanded = !descExpanded">
          {{ descExpanded ? $t('routeDetails.descriptionLess') : $t('routeDetails.descriptionMore') }}
        </button>
      </section>

      <div v-if="tagList.length" class="px-4 pt-3 flex flex-wrap items-center gap-1.5 text-[13px]">
        <span v-for="tag in tagList" :key="tag" class="rounded-md border border-border px-2 py-0.5 text-ink2">{{ tag }}</span>
      </div>
      <!-- «Погода ⌄» - скролл к прогнозу; пока виден хоть краешек прогноза, кнопки нет. -->
      <div v-if="!weatherVisible" class="flex justify-center pt-3">
        <button type="button" data-scroll-weather class="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-semibold text-ink2 hover:bg-accent hover:text-foreground" @click="scrollToWeather">
          {{ $t('routeDetails.weatherShort') }}<ChevronDown class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <!-- «Условия сейчас» -->
      <div v-if="conditions" class="px-4 pt-4">
        <ConditionsNowChip :conditions="conditions" />
      </div>
      <div class="h-4" aria-hidden="true" />

      <div ref="weatherRef"><RouteWeatherWidget :route="activeRoute" /></div>

      </template>
      <!-- Дни маршрута - своя вкладка (только у многодневных). -->
      <div v-else-if="tab === 'days'" data-route-days class="px-4 pt-4 pb-6 space-y-2">
        <p class="text-[13px] text-ink2">{{ formatStages(routeSegments) }}</p>
        <div
          v-for="seg in routeSegments"
          :key="seg.day_index"
          class="flex items-start justify-between gap-2 px-3 py-2.5 rounded-xl bg-[hsl(var(--muted)/0.45)] border border-[hsl(var(--border)/0.6)]"
        >
          <div class="min-w-0 flex-1">
            <p class="text-[12px] font-semibold truncate">{{ seg.label }}</p>
            <p v-if="seg.note" class="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">{{ seg.note }}</p>
          </div>
          <div class="shrink-0 text-right text-[11px] font-semibold">
            <p>{{ formatDistance(seg.distance_km) }}</p>
            <p class="text-[hsl(var(--muted-foreground))] mt-0.5">↗{{ formatElevation(seg.elevation_gain_m) }}</p>
          </div>
        </div>
      </div>
      <!-- Отзывы: сводка, лента, «Прошли этот маршрут? - Оставить отзыв». -->
      <div v-else-if="tab === 'reviews'" class="pt-3">
        <RouteReviews :route-id="activeRoute.id" :route-name="activeRoute.name" :owner-user-id="activeRoute.owner_user_id" />
      </div>
    </div>
  </div>

  <div v-else class="flex items-center justify-center h-full text-[hsl(var(--muted-foreground))] text-sm">
    {{ $t('routeDetails.selectRoute') }}
  </div>

  <RouteRegistrationDialog
    v-if="showRegisterDialog && activeRoute"
    :presetRoute="{ id: activeRoute.id, name: activeRoute.name, durationMinutes: activeRoute.estimated_duration_minutes }"
    @close="showRegisterDialog = false"
    @registered="showRegisterDialog = false"
  />

  <TechnicalGradeModal
    v-if="showGradeModal"
    :currentGrade="activeRoute?.technical_grade"
    @close="showGradeModal = false"
  />

  <GpxDownloadModal
    v-if="showGpxModal && activeRoute"
    :gpxUrl="localGpx?.url ?? gpxDownloadUrl"
    :fileName="localGpx?.fileName"
    @close="closeGpxModal"
  />

</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { X, AlertTriangle, TrendingUp, TrendingDown, ChevronDown, ChevronLeft, Ruler, Clock, Info, Tent, Coffee } from "lucide-vue-next";
import { legStats } from "../utils/profileLegs";
import { sliceStats } from "../utils/profileSlice";
import RatingStar from "./route/RatingStar.vue";
import RouteActionRow from "./route/RouteActionRow.vue";
import ConditionsNowChip from "./route/ConditionsNowChip.vue";
import { googleMapsUrl, openInTwoGis, twoGisWebUrl } from "../utils/twoGis";
import GradeBadge from "./ui/GradeBadge.vue";
import { useConditionsNow } from "../composables/useConditionsNow";
import api, { API_BASE_URL } from "../api";
import { useAuthStore } from "../store/auth";
import { useSavedRoutesStore } from "../store/savedRoutes";
import { buildRouteShareUrl, shareRouteLink } from "../utils/routeShare";
import { useLoginGateStore } from "../store/loginGate";
import { useRouteActionsStore } from "../store/routeActions";
import { useToast } from "../composables/useToast";
import type { RouteDaySegment, RouteElevationProfile, RouteMapItem } from "../types/routes";
import { gradeAccentColor } from "../utils/routeGrade";
import { elevationAndGradeAtKm } from "../utils/routeCursor";
import { formatHikingDuration } from "../utils/formatDuration";
import { splitRouteCode } from "../utils/routeName";
import { startDirections } from "../utils/startDirections";
import { routeSource } from "../utils/routeFilters";
import RouteWeatherWidget from "./RouteWeatherWidget.vue";
import TechnicalGradeModal from "./TechnicalGradeModal.vue";
import InfoTooltip from "./InfoTooltip.vue";
import VerifiedMark from "./ui/VerifiedMark.vue";
import RouteReviews from "./route/RouteReviews.vue";
import RouteRegistrationDialog from "./RouteRegistrationDialog.vue";
import GpxDownloadModal from "./GpxDownloadModal.vue";
import { buildGpxXml, gpxFileName } from "../utils/gpxExport";

const { t } = useI18n();

const props = defineProps<{
  selectedRouteId?: number | null;
  compact?: boolean;
  // Дистанция (км) вдоль маршрута, где стоит общий "курсор маршрута" -
  // двусторонне синхронизирован с перетаскиваемым маркером на карте через App.vue.
  cursorDistanceKm?: number | null;
  // Выставляется MobileRouteDetailSheet.vue, пока bottom sheet ниже
  // максимальной высоты, чтобы драг по контенту растягивал sheet вместо
  // скролла - см. scrollable body выше.
  scrollLocked?: boolean;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "back"): void;
  (e: "show-login"): void;
  (e: "tab-select"): void;
  (e: "update:cursorDistanceKm", km: number | null): void;
  (e: "open-profile", username: string): void;
}>();

const auth = useAuthStore();
const savedRoutes = useSavedRoutesStore();
const loginGate = useLoginGateStore();

const activeRoute = ref<RouteMapItem | null>(null);
const tab = ref<"overview" | "days" | "reviews">("overview");
watch(tab, () => {
  if (scrollBodyRef.value) scrollBodyRef.value.scrollTop = 0;
});
const selectTab = (next: "overview" | "days" | "reviews") => {
  tab.value = next;
  emit("tab-select");
};
// «Дни» - только у многодневных маршрутов (больше одного сегмента).
const tabDefs = computed(() => [
  { key: "overview" as const, label: t("routeDetails.tabs.overview"), count: 0 },
  ...(routeSegments.value.length > 1 ? [{ key: "days" as const, label: t("routeDetails.tabs.days"), count: routeSegments.value.length }] : []),
  { key: "reviews" as const, label: t("routeDetails.tabs.reviews"), count: activeRoute.value?.rating_count ?? 0 },
]);
const descExpanded = ref(false);
const descLong = computed(() => (activeRoute.value?.description?.length ?? 0) > 140);
const authorProfileHref = computed(() => `${import.meta.env.BASE_URL}user/${encodeURIComponent(activeRoute.value?.author_username ?? "")}`);
// Профиль автора открывается панелью поверх карты (SPA-переход, App.vue ->
// useSearchNavigation.openProfile); href остаётся для «открыть в новой вкладке».
const onAuthorClick = (e: MouseEvent) => {
  const username = activeRoute.value?.author_username;
  if (!username || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  emit("open-profile", username);
};
const hasRating = computed(() => !!activeRoute.value?.rating_count && activeRoute.value?.rating != null);
const hasStatusNotes = computed(() => !!activeRoute.value?.status && activeRoute.value.status !== "active");
const routeName = computed(() => splitRouteCode(activeRoute.value?.name ?? ""));
const directions = computed(() => startDirections(activeRoute.value?.coordinates));
const isCommunity = computed(() => activeRoute.value != null && routeSource(activeRoute.value) === "community");
const showGradeModal = ref(false);
const showGpxModal = ref(false);
// Есть исходный GPX-файл (загруженный админом) - окно со скачиванием с сервера;
// маршруты, нарисованные в редакторе, файла не имеют - GPX собирается на клиенте
// из геометрии и точек (как «Скачать GPX» в самом редакторе). Приватность не
// важна: свои маршруты владелец видит и скачивает всегда.
const hasStoredGpx = computed(() => Boolean(activeRoute.value?.gpx_object_key));
const canExportClientGpx = computed(() => (activeRoute.value?.coordinates?.length ?? 0) >= 2);
const canDownload = computed(() => hasStoredGpx.value || canExportClientGpx.value);
// Тот же диалог с пояснением, что у каталожных: у маршрута без файла ссылка -
// локально собранный GPX (blob), он освобождается при закрытии окна.
const localGpx = ref<{ url: string; fileName: string } | null>(null);
const revokeLocalGpx = () => {
  if (localGpx.value) URL.revokeObjectURL(localGpx.value.url);
  localGpx.value = null;
};
const closeGpxModal = () => {
  showGpxModal.value = false;
  revokeLocalGpx();
};
const openGpxDownload = () => {
  const route = activeRoute.value;
  if (!route) return;
  if (hasStoredGpx.value) {
    revokeLocalGpx();
    showGpxModal.value = true;
    return;
  }
  if (!canExportClientGpx.value) return;
  revokeLocalGpx();
  const xml = buildGpxXml(
    route.name,
    (route.coordinates ?? []).map(([lon, lat]) => [lon, lat] as [number, number]),
    (route.pois ?? []).map((p) => ({ name: p.name, description: p.description, poi_type: p.poi_type ?? "note", latitude: p.latitude, longitude: p.longitude }))
  );
  localGpx.value = { url: URL.createObjectURL(new Blob([xml], { type: "application/gpx+xml" })), fileName: gpxFileName(route.name) };
  showGpxModal.value = true;
};
const { message: toastMessage, show: showToast } = useToast();

// Точка старта в 2ГИС (href - для «открыть в новой вкладке», клик - с запасным путём).
const twoGisUrl = computed(() => (directions.value ? twoGisWebUrl(directions.value.lat, directions.value.lon) : ""));
const googleMapsLink = computed(() => (directions.value ? googleMapsUrl(directions.value.lat, directions.value.lon) : ""));
const open2gis = () => {
  if (directions.value) openInTwoGis(directions.value.lat, directions.value.lon);
};

const openGoogleMaps = () => {
  if (googleMapsLink.value) window.open(googleMapsLink.value, "_blank", "noopener,noreferrer");
};
const copyStart = async () => {
  if (!directions.value) return;
  try {
    await navigator.clipboard.writeText(directions.value.label);
    showToast(t("routeDetails.gettingThere.copied"));
  } catch {
    showToast(directions.value.label);
  }
};

// Публичный эндпоинт, авторизация не нужна - обычная <a download> в
// GpxDownloadModal.vue, без token-фолбэка resolveMediaUrl.
const gpxDownloadUrl = computed(() =>
  activeRoute.value ? `${API_BASE_URL}/routes/${activeRoute.value.id}/gpx` : ""
);

// Измеряет шапку + видимую в компактном режиме сводку (заголовок до строки
// Format), чтобы MobileRouteDetailSheet.vue мог подогнать высоту
// компактного снапа "sm" ровно под это - см. compactHeight ниже.
const headerRef = ref<HTMLElement | null>(null);
const compactSummaryRef = ref<HTMLElement | null>(null);
const scrollBodyRef = ref<HTMLElement | null>(null);
const weatherRef = ref<HTMLElement | null>(null);
const weatherVisible = ref(false);
let weatherObserver: IntersectionObserver | null = null;
watch([weatherRef, scrollBodyRef], ([el, root]) => {
  weatherObserver?.disconnect();
  weatherObserver = null;
  if (!el || !root || typeof IntersectionObserver === "undefined") return;
  weatherObserver = new IntersectionObserver(([entry]) => (weatherVisible.value = entry.isIntersecting), { root });
  weatherObserver.observe(el);
});
onBeforeUnmount(() => weatherObserver?.disconnect());
const scrollToWeather = () => weatherRef.value?.scrollIntoView({ behavior: "smooth", block: "start" });
const headerHeight = ref(0);
const compactSummaryHeight = ref(0);
let headerObserver: ResizeObserver | null = null;
let compactSummaryObserver: ResizeObserver | null = null;

watch(headerRef, (el) => {
  headerObserver?.disconnect();
  if (!el) return;
  // entry.contentRect не включает padding, а у headerRef есть вертикальные
  // отступы (pt-4/pb-2) - высоту нужно брать с самого элемента (border-box),
  // а не из ResizeObserver entry, иначе измеренная высота занижена на
  // величину padding.
  headerObserver = new ResizeObserver(() => {
    headerHeight.value = el.getBoundingClientRect().height;
  });
  headerObserver.observe(el);
});
watch(compactSummaryRef, (el) => {
  compactSummaryObserver?.disconnect();
  if (!el) return;
  compactSummaryObserver = new ResizeObserver(() => {
    compactSummaryHeight.value = el.getBoundingClientRect().height;
  });
  compactSummaryObserver.observe(el);
});
onBeforeUnmount(() => {
  revokeLocalGpx();
  headerObserver?.disconnect();
  compactSummaryObserver?.disconnect();
  // на случай, если панель размонтируется во время драга по графику высот
  window.removeEventListener("pointermove", onProfilePointerMove);
  window.removeEventListener("pointerup", onProfilePointerUp);
  window.removeEventListener("pointercancel", onProfilePointerUp);
});
const compactHeight = computed(() => headerHeight.value + compactSummaryHeight.value);

const routeSegments = computed<RouteDaySegment[]>(() => activeRoute.value?.daily_segments || []);

const maxElevation = computed(() => activeRoute.value?.elevation_profile?.max_elevation_m ?? null);

const formattedDuration = computed(() =>
  formatHikingDuration(activeRoute.value?.estimated_duration_minutes, t("units.h"), t("units.decimal"))
);

const isSaved = computed(() => activeRoute.value != null && savedRoutes.isSaved(activeRoute.value.id));

const gradeLabel = (grade: number) => t(`routeDetails.grade.${grade}`);

const statusDotClass = (s?: string) => {
  if (s === "active") return "bg-[hsl(var(--success))]";
  if (s === "draft") return "bg-[hsl(var(--warning))]";
  if (s === "closed") return "bg-[hsl(var(--danger))]";
  return "bg-[hsl(var(--muted-foreground))]";
};

const toggleSave = async () => {
  if (!activeRoute.value || !auth.isAuthenticated) return;
  const id = activeRoute.value.id;
  if (savedRoutes.isSaved(id)) {
    await savedRoutes.unsave(id);
  } else {
    await savedRoutes.save(id);
    showToast(t("routeDetails.toast.saved"));
  }
};

// Отложенное входом «Сохранить» - только сохранение: если маршрут уже был
// сохранён (с другого устройства), повторное нажатие не должно его снять.
const saveRoute = async () => {
  if (!activeRoute.value || !auth.isAuthenticated) return;
  if (savedRoutes.isSaved(activeRoute.value.id)) return;
  await savedRoutes.save(activeRoute.value.id);
  showToast(t("routeDetails.toast.saved"));
};

const shareRoute = async () => {
  if (!activeRoute.value) return;
  const url = buildRouteShareUrl(location.origin, activeRoute.value);
  const outcome = await shareRouteLink(url, activeRoute.value.name);
  if (outcome === "copied") showToast(t("routeDetails.toast.linkCopied"));
  else if (outcome === "failed") showToast(url);
};

// Лайк маршрута (сердечко в шапке деталей): гость - на логин, иначе
// POST/DELETE /routes/{id}/like и обновление счётчика из ответа.
const showRegisterDialog = ref(false);
const openRegister = () => {
  loginGate.requireLogin(auth.isAuthenticated, () => {
    showRegisterDialog.value = true;
  });
};

// Мобильный: кнопки маршрута живут в нижней панели App.vue - публикуем туда
// состояние и обработчики, пока шторка открыта.
const routeActions = useRouteActionsStore();
watchEffect(() => {
  if (!props.compact || !activeRoute.value) return;
  routeActions.publish({
    saved: isSaved.value,
    canDownload: canDownload.value,
    save: () => (auth.isAuthenticated ? toggleSave() : loginGate.open(saveRoute)),
    share: shareRoute,
    hasStart: !!directions.value,
    copyStart,
    open2gis,
    openGoogleMaps,
    register: openRegister,
    download: openGpxDownload,
  });
});
onBeforeUnmount(() => {
  if (props.compact) routeActions.clear();
});

const tagList = computed(() => {
  const tags = activeRoute.value?.tags;
  if (!tags) return [];
  return Array.isArray(tags) ? tags : [tags];
});

const profileChart = computed(() => {
  const profile = activeRoute.value?.elevation_profile;
  if (!profile || !Array.isArray(profile.points) || profile.points.length < 2) return null;
  const W = 320, T = 6, B = 90;
  const totalDist = profile.distance_km || profile.points.at(-1)!.distance_km || 1;
  const minEl = profile.min_elevation_m;
  const range = Math.max(profile.max_elevation_m - minEl, 1);
  const tx = (d: number) => (Math.max(0, Math.min(d, totalDist)) / totalDist) * W;
  const ty = (e: number) => B - ((e - minEl) / range) * (B - T);
  const line = profile.points.map((p, i) => `${i === 0 ? "M" : "L"} ${tx(p.distance_km).toFixed(1)} ${ty(p.elevation_m).toFixed(1)}`).join(" ");
  return {
    linePath: line,
    areaPath: `${line} L ${W} ${B} L 0 ${B} Z`,
    markers: (profile.markers || []).map((m) => ({
      ...m,
      x: tx(m.distance_km),
      y: ty(typeof m.elevation_m === "number" ? m.elevation_m : minEl),
    })),
  };
});

// "Круглый" шаг (1/2/5 × 10^n) для делений сетки, чтобы деления попадали на
// круглые значения (100м/1км), а не на произвольный шаг от равномерного
// деления диапазона.
const niceStep = (range: number, targetCount: number): number => {
  const rough = range / Math.max(targetCount, 1);
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const residual = rough / magnitude;
  if (residual > 5) return 10 * magnitude;
  if (residual > 2) return 5 * magnitude;
  if (residual > 1) return 2 * magnitude;
  return magnitude;
};

const elevationTicks = computed(() => {
  const profile = activeRoute.value?.elevation_profile;
  if (!profile) return [];
  const T = 6, B = 90;
  const minEl = profile.min_elevation_m;
  const maxEl = profile.max_elevation_m;
  const range = Math.max(maxEl - minEl, 1);
  const step = niceStep(range, 4);
  const ticks: { value: number; y: number; pct: number; label: string }[] = [];
  for (let v = Math.ceil(minEl / step) * step; v <= maxEl + 1e-6; v += step) {
    const y = B - ((v - minEl) / range) * (B - T);
    ticks.push({ value: v, y, pct: (y / 96) * 100, label: `${Math.round(v)}${t("units.m")}` });
  }
  return ticks;
});

const distanceTicks = computed(() => {
  const profile = activeRoute.value?.elevation_profile;
  if (!profile || !Array.isArray(profile.points) || !profile.points.length) return [];
  const W = 320;
  const totalDist = profile.distance_km || profile.points.at(-1)!.distance_km || 1;
  const step = niceStep(totalDist, 5);
  const ticks: { value: number; x: number; pct: number; label: string }[] = [];
  for (let d = 0; d <= totalDist + 1e-6; d += step) {
    const clamped = Math.min(d, totalDist);
    const x = (clamped / totalDist) * W;
    const label = step < 1 ? `${clamped.toFixed(1)}${t("units.km")}` : `${Math.round(clamped)}${t("units.km")}`;
    ticks.push({ value: d, x, pct: (x / W) * 100, label });
  }
  return ticks;
});

const markerIcon = (kind: string) => (kind === "night-stop" ? Tent : kind === "rest-stop" ? Coffee : null);

// Выбранная тапом контрольная точка профиля: длина/набор/спуск от предыдущей.
const selectedMarkerIdx = ref<number | null>(null);
const profileLegs = computed(() => {
  const profile = activeRoute.value?.elevation_profile;
  return profile ? legStats(profile.points || [], profile.markers || []) : [];
});
const selectedLeg = computed(() => {
  const i = selectedMarkerIdx.value;
  const marker = i == null ? null : activeRoute.value?.elevation_profile?.markers?.[i];
  const leg = i == null ? null : profileLegs.value[i];
  return marker && leg ? { label: marker.label, ...leg } : null;
});
watch(() => activeRoute.value?.id, () => { selectedMarkerIdx.value = null; });

const markerColor = (kind: string) => {
  if (kind === "start") return "hsl(155,55%,42%)";
  if (kind === "finish") return "hsl(222,25%,11%)";
  return "hsl(38,92%,50%)";
};

// --- Курсор маршрута на графике высот ---
//
// График одномерный: высота (y) всегда выводится из дистанции (x)
// интерполяцией точек профиля, поэтому "прилипание к линии" получается
// автоматически - любому x, куда кликнул/тащит пользователь, соответствует
// ровно одна точка на линии. Ограничивать нужно только x, в пределах длины маршрута.

const profileSvgRef = ref<SVGSVGElement | null>(null);

// Уникален на экземпляр компонента - см. комментарий у <linearGradient> в
// template о том, почему общий статичный id ломал заливку на мобильном.
const gradientId = `rdp-fill-${Math.random().toString(36).slice(2)}`;

const profileCursor = computed(() => {
  const km = props.cursorDistanceKm;
  const profile = activeRoute.value?.elevation_profile;
  if (typeof km !== "number" || !profile || !Array.isArray(profile.points) || profile.points.length < 2) {
    return null;
  }
  const W = 320, T = 6, B = 90;
  const totalDist = profile.distance_km || profile.points.at(-1)!.distance_km || 1;
  const clamped = Math.max(0, Math.min(km, totalDist));
  const at = elevationAndGradeAtKm(profile.points, clamped);
  if (!at) return null;
  const minEl = profile.min_elevation_m;
  const range = Math.max(profile.max_elevation_m - minEl, 1);
  return {
    x: (clamped / totalDist) * W,
    y: B - ((at.elevationM - minEl) / range) * (B - T),
    behindKm: clamped,
    aheadKm: totalDist - clamped,
    elevationM: at.elevationM,
    gradePct: at.gradePct,
  };
});

// Сторона бейджа относительно точки курсора - "right", пока точка в левой
// половине графика, "left" после середины, чтобы бейдж не перекрывал свою же точку.
const cursorBadgeSide = computed<"left" | "right">(() =>
  profileCursor.value && profileCursor.value.x < 160 ? "right" : "left"
);

const CURSOR_BADGE_GAP_PX = 10;

const cursorBadgeStyle = computed(() => {
  const cursor = profileCursor.value;
  if (!cursor) return {};
  const xPct = (cursor.x / 320) * 100;
  const yPct = Math.min(Math.max((cursor.y / 96) * 100, 8), 92);
  return cursorBadgeSide.value === "right"
    ? { top: `${yPct}%`, left: `calc(${xPct}% + ${CURSOR_BADGE_GAP_PX}px)` }
    : { top: `${yPct}%`, right: `calc(${100 - xPct}% + ${CURSOR_BADGE_GAP_PX}px)` };
});

// Pointer Events + setPointerCapture - единый код для мыши/тача/пера, тот же
// приём, что в AvatarCropDialog.vue. pointerdown сразу ставит курсор
// (клик-переход), последующие движения при захвате его тащат.
let profileDragPointerId: number | null = null;
// Пока курсор тащат - полоса цифр показывает участок «старт-курсор»; на отпускание - итоги.
const profileDragging = ref(false);

const emitCursorFromClientX = (clientX: number) => {
  const svg = profileSvgRef.value;
  const profile = activeRoute.value?.elevation_profile;
  if (!svg || !profile) return;
  const rect = svg.getBoundingClientRect();
  if (rect.width === 0) return;
  const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  const totalDist = profile.distance_km || profile.points.at(-1)!.distance_km || 1;
  emit("update:cursorDistanceKm", frac * totalDist);
};

const onProfilePointerMove = (e: PointerEvent) => {
  if (e.pointerId !== profileDragPointerId) return;
  emitCursorFromClientX(e.clientX);
};

const onProfilePointerUp = (e: PointerEvent) => {
  if (e.pointerId !== profileDragPointerId) return;
  profileDragPointerId = null;
  profileDragging.value = false;
  window.removeEventListener("pointermove", onProfilePointerMove);
  window.removeEventListener("pointerup", onProfilePointerUp);
  window.removeEventListener("pointercancel", onProfilePointerUp);
};

const onProfilePointerDown = (e: PointerEvent) => {
  profileDragPointerId = e.pointerId;
  profileDragging.value = true;
  try {
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
  } catch {
    // NotFoundError в редких edge-case - драг всё равно работает через
    // обработчики на window ниже, capture лишь удобство (как в AvatarCropDialog).
  }
  emitCursorFromClientX(e.clientX);
  window.addEventListener("pointermove", onProfilePointerMove);
  window.addEventListener("pointerup", onProfilePointerUp);
  window.addEventListener("pointercancel", onProfilePointerUp);
};

const statusLabel = (s?: string) => {
  if (s === "active") return t("routeStatus.active");
  if (s === "draft") return t("routeStatus.draft");
  if (s === "closed") return t("routeStatus.closed");
  return t("routeStatus.unknown");
};

const statusBadgeClass = (s?: string) => {
  if (s === "active") return "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]";
  if (s === "draft") return "bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]";
  if (s === "closed") return "bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]";
  return "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]";
};

const formatDistance = (v?: number | null) => v != null ? `${v.toFixed(1)}${t("units.km")}` : t("routeStatus.unknown");
const formatElevation = (v?: number | null) => v != null ? `${Math.round(v)}${t("units.m")}` : t("routeStatus.unknown");
const formatStages = (segs: RouteDaySegment[]) => {
  const n = segs.length || 1;
  const word = n === 1 ? t("routeDetails.dayWord.one") : n < 5 ? t("routeDetails.dayWord.few") : t("routeDetails.dayWord.many");
  return `${n} ${word}`;
};
const formatProfileRange = (r: RouteMapItem | null) => {
  const p = r?.elevation_profile as RouteElevationProfile | null | undefined;
  if (!p) return t("routeStatus.unknown");
  return `${Math.round(p.min_elevation_m)}–${Math.round(p.max_elevation_m)} ${t("units.m")}`;
};

// GET /routes/{id} отдаёт coordinates/daily_segments/elevation_profile,
// которых больше нет в облегчённом списке GET /routes - подгружается
// отдельно при выборе маршрута, а не ищется в уже загруженном списке.
let currentLoadToken = 0;

const loadActiveRoute = async (routeId?: number | null) => {
  const token = ++currentLoadToken;
  if (typeof routeId !== "number") {
    activeRoute.value = null;
    return;
  }
  try {
    const res = await api.get<RouteMapItem>(`/routes/${routeId}`);
    if (token !== currentLoadToken) return;
    activeRoute.value = res.data;
    // Просмотр: сервер сам дедуплицирует (раз в сутки на зрителя) и не
    // считает владельца, поэтому достаточно дёргать при каждом открытии.
    void api.post(`/routes/${routeId}/view`).catch(() => undefined);
  } catch {
    if (token === currentLoadToken) activeRoute.value = null;
  }
};

watch(() => props.selectedRouteId, (routeId) => {
  tab.value = "overview";
  descExpanded.value = false;
  loadActiveRoute(routeId);
}, { immediate: true });

// «Условия сейчас» - ближайшая метеостанция к центру маршрута.
const { conditions } = useConditionsNow(activeRoute);

// Легенда цифр в «Обзоре»: дистанция, набор, спуск, макс. высота, время.
const statItems = computed(() => {
  const r = activeRoute.value;
  if (!r) return [];
  const fmt = (v: number | null | undefined, f: (n: number) => string) => (v != null ? f(v) : "—");
  const slice = profileDragging.value && profileCursor.value && r.elevation_profile?.points
    ? sliceStats(r.elevation_profile.points, props.cursorDistanceKm ?? 0, r.estimated_duration_minutes ?? null)
    : null;
  const distance = slice ? slice.distanceKm : r.distance_km;
  const gain = slice ? slice.gainM : r.elevation_gain_m;
  const loss = slice ? slice.lossM : r.elevation_loss_m;
  const height = slice ? slice.elevationM : maxElevation.value;
  const time = slice ? (slice.timeMin != null ? formatHikingDuration(slice.timeMin, t("units.h"), t("units.decimal")) : "") : formattedDuration.value;
  return [
    { label: t("routeDetails.plate.distance"), value: fmt(distance, (n) => n.toFixed(1)), unit: t("units.km"), hint: t("routeDetails.stats.distanceHint") },
    { label: t("routeDetails.plate.gain"), value: fmt(gain, (n) => `+${Math.round(n)}`), unit: t("units.m"), hint: t("routeDetails.stats.gainHint") },
    { label: t("routeDetails.plate.loss"), value: fmt(loss, (n) => `−${Math.round(n)}`), unit: t("units.m") },
    { label: t("routeDetails.plate.max"), value: fmt(height, (n) => String(Math.round(n))), unit: t("units.m"), hint: slice ? undefined : t("routeDetails.stats.maxElevationHint") },
    { label: t("routeDetails.plate.time"), value: time || "—", tip: !slice && formattedDuration.value ? t("routeDetails.estimatedTimeDisclaimer") : undefined },
  ];
});

defineExpose({ activeRoute, compactHeight, headerHeight });
</script>
